import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import MainLayout from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { RightPanel } from '../components/ui/right-panel';
import { toast } from 'sonner';
import {
  Shield, ChevronRight, Check, X, Save, RefreshCw, Info, Gauge,
  CheckCircle2, Circle, AlertTriangle
} from 'lucide-react';

const LEVEL_LABELS = {
  2: { label: 'Herhaalbaar', color: 'bg-blue-100 text-blue-700' },
  3: { label: 'Gedefinieerd', color: 'bg-indigo-100 text-indigo-700' },
  4: { label: 'Beheerst', color: 'bg-violet-100 text-violet-700' },
  5: { label: 'Optimaliserend', color: 'bg-emerald-100 text-emerald-700' },
};

const LEVEL_CONDITIONS = {
  level_2: [
    { key: 'l2a', label: 'Eigenaar actief met mandaat' },
    { key: 'l2b', label: 'Werkwijze en planning vastgesteld' },
    { key: 'l2c', label: 'Minimaal 2 uitvoeringen, ≥80% op tijd' },
    { key: 'l2d', label: 'Afwijkingen hebben eigenaar en termijn' },
  ],
  level_3: [
    { key: 'l3a', label: 'Organisatiebrede procedure aanwezig' },
    { key: 'l3b', label: '≥90% eenheden, ≥90% getraind' },
    { key: 'l3c', label: '≥90% tijdig, uitzonderingen goedgekeurd' },
    { key: 'l3d', label: 'Alle toetsvelden (Bijlage A) waar' },
  ],
  level_4: [
    { key: 'l4a', label: 'KPI\'s met baseline en grenzen vastgelegd' },
    { key: 'l4b', label: '6 maanden gemeten, ≥95% meetpunten' },
    { key: 'l4c', label: 'Variatie-analyse, voorspelbaarheid aangetoond' },
    { key: 'l4d', label: 'Management reviews, ≥90% acties tijdig' },
  ],
  level_5: [
    { key: 'l5a', label: 'Oorzaakanalyse voor terugkerende afwijkingen' },
    { key: 'l5b', label: '≥2 verbetercycli in 365 dagen' },
    { key: 'l5c', label: 'Positief effect ≥3 maanden stabiel' },
    { key: 'l5d', label: 'Verbetering in alle eenheden ingevoerd' },
  ],
};

import { SectionTabs, SECTIONS } from '../components/enterprise';

const MaturityPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [domains, setDomains] = useState([]);
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchIndicators = useCallback(async () => {
    try {
      const res = await api.get('/scores/indicators');
      setDomains(res.data.domains);
    } catch (e) {
      toast.error('Kon indicatoren niet laden');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchIndicators(); }, [fetchIndicators]);

  const openIndicator = (indicator) => {
    setSelectedIndicator(indicator);
    setEditData({
      level_2: indicator.level_2 || {},
      level_3: indicator.level_3 || {},
      level_4: indicator.level_4 || {},
      level_5: indicator.level_5 || {},
    });
  };

  const toggleCondition = (levelKey, condKey) => {
    setEditData(prev => ({
      ...prev,
      [levelKey]: {
        ...prev[levelKey],
        [condKey]: !prev[levelKey]?.[condKey],
      },
    }));
  };

  const toggleField = (fieldName) => {
    setEditData(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  const saveAssessment = async () => {
    if (!selectedIndicator) return;
    setSaving(true);
    try {
      const payload = { ...editData };
      if (selectedIndicator.fields) {
        selectedIndicator.fields.forEach(f => {
          if (editData[f] !== undefined) payload[f] = editData[f];
        });
      }
      const res = await api.put(`/scores/indicators/${selectedIndicator.id}`, payload);
      toast.success(`${selectedIndicator.id} bijgewerkt naar niveau ${res.data.level || 1}`);
      await fetchIndicators();
      // Update selected indicator with new data
      setSelectedIndicator(prev => prev ? { ...prev, level: res.data.level } : null);
    } catch (e) {
      toast.error('Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const getAssessedCount = (domain) => domain.indicators.filter(i => i.assessed).length;
  const getDomainLevel = (domain) => {
    const levels = domain.indicators.map(i => i.level).filter(l => l != null);
    return levels.length === domain.indicators.length ? Math.min(...levels) : null;
  };

  const calculateEditLevel = () => {
    for (const [levelNum, levelKey] of [[5, 'level_5'], [4, 'level_4'], [3, 'level_3'], [2, 'level_2']]) {
      const conds = editData[levelKey] || {};
      if (!Object.values(conds).every(v => v === true)) {
        return levelNum - 1;
      }
    }
    return 5;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-white">
          <div className="mx-auto max-w-5xl px-6 py-10">
            <Skeleton className="h-8 w-80 mb-8" />
            <div className="space-y-4">
              {[1,2,3].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-white" data-testid="maturity-page">
        <SectionTabs tabs={SECTIONS.beoordelen.tabs} />
        <div className="mx-auto max-w-5xl px-6 py-10">

          <header className="mb-8">
            <h1 className="text-2xl font-semibold tracking-tight text-black">Volwassenheidsbeoordeling</h1>
            <p className="text-sm text-neutral-500 mt-1">Beoordeel de 24 CMM-indicatoren verdeeld over 6 domeinen</p>
          </header>

          <div className="flex gap-6">
            {/* DOMAIN LIST */}
            <div className={`${selectedIndicator ? 'w-1/2' : 'w-full'} space-y-4 transition-all`}>
              {domains.map(domain => {
                const assessed = getAssessedCount(domain);
                const domainLevel = getDomainLevel(domain);
                return (
                  <Card key={domain.domain} className="border-neutral-200" data-testid={`domain-${domain.domain}`}>
                    <CardContent className="p-0">
                      <div className="flex items-center justify-between p-4 border-b border-neutral-100">
                        <div className="flex items-center gap-3">
                          <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${domainLevel != null ? (domainLevel >= 3 ? 'bg-emerald-50' : 'bg-amber-50') : 'bg-neutral-50'}`}>
                            <Gauge className={`h-4 w-4 ${domainLevel != null ? (domainLevel >= 3 ? 'text-emerald-600' : 'text-amber-600') : 'text-neutral-400'}`} strokeWidth={1.5} />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-black">{domain.label}</p>
                            <p className="text-[10px] text-neutral-500">{Math.round(domain.weight * 100)}% gewicht — {assessed}/{domain.indicators.length} beoordeeld</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {domainLevel != null && (
                            <Badge className={LEVEL_LABELS[domainLevel]?.color || 'bg-neutral-100 text-neutral-600'}>
                              Niv. {domainLevel}
                            </Badge>
                          )}
                          <div className="h-1.5 w-16 bg-neutral-100 rounded-full overflow-hidden">
                            <div className="h-full bg-black rounded-full transition-all" style={{ width: `${(assessed / domain.indicators.length) * 100}%` }} />
                          </div>
                        </div>
                      </div>
                      <div className="divide-y divide-neutral-50">
                        {domain.indicators.map(ind => (
                          <button key={ind.id} onClick={() => openIndicator(ind)}
                            className={`w-full flex items-center justify-between px-4 py-3 text-left hover:bg-neutral-50 transition-colors ${selectedIndicator?.id === ind.id ? 'bg-neutral-50 border-l-2 border-black' : ''}`}
                            data-testid={`indicator-${ind.id}`}>
                            <div className="flex items-center gap-3 min-w-0">
                              <div className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 text-[10px] font-bold ${
                                ind.level != null ? (ind.level >= 3 ? 'bg-emerald-100 text-emerald-700' : ind.level >= 2 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700') : 'bg-neutral-100 text-neutral-400'
                              }`}>
                                {ind.level != null ? ind.level : '-'}
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-black">{ind.id}: {ind.label}</p>
                                {ind.updated_at && <p className="text-[10px] text-neutral-400">{ind.assessor}, {new Date(ind.updated_at).toLocaleDateString('nl-NL')}</p>}
                              </div>
                            </div>
                            <ChevronRight className="h-3.5 w-3.5 text-neutral-300 shrink-0" strokeWidth={1.5} />
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* INDICATOR DETAIL PANEL */}
            {selectedIndicator && (
              <div className="w-1/2 sticky top-6 self-start" data-testid="indicator-detail">
                <Card className="border-neutral-200">
                  <CardContent className="p-0">
                    <div className="flex items-center justify-between p-4 border-b border-neutral-100">
                      <div>
                        <p className="text-sm font-semibold text-black">{selectedIndicator.id}: {selectedIndicator.label}</p>
                        <p className="text-[10px] text-neutral-500 mt-0.5">
                          Huidig niveau: <span className="font-bold">{selectedIndicator.level || 1}</span> → Preview: <span className="font-bold text-blue-600">{calculateEditLevel()}</span>
                        </p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedIndicator(null)} className="h-7 w-7 p-0">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="p-4 space-y-5 max-h-[65vh] overflow-y-auto">
                      {/* L3D Fields (Bijlage A) */}
                      {selectedIndicator.fields?.length > 0 && (
                        <div>
                          <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-2">Toetsvelden (Bijlage A)</p>
                          <div className="space-y-2">
                            {selectedIndicator.fields.map(field => (
                              <label key={field} className="flex items-center gap-3 p-2 rounded-lg hover:bg-neutral-50 cursor-pointer" data-testid={`field-${field}`}>
                                <Switch checked={editData[field] || false} onCheckedChange={() => toggleField(field)} />
                                <span className="text-xs text-neutral-700">{field.replace(/_/g, ' ')}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* CMM Level Conditions */}
                      {Object.entries(LEVEL_CONDITIONS).map(([levelKey, conditions]) => {
                        const levelNum = parseInt(levelKey.replace('level_', ''));
                        const allPassed = conditions.every(c => editData[levelKey]?.[c.key] === true);
                        const prevPassed = levelNum === 2 || (
                          LEVEL_CONDITIONS[`level_${levelNum - 1}`]?.every(c => editData[`level_${levelNum - 1}`]?.[c.key] === true)
                        );
                        return (
                          <div key={levelKey} className={`${!prevPassed ? 'opacity-50' : ''}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={allPassed ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-100 text-neutral-600'} data-testid={`level-badge-${levelNum}`}>
                                {allPassed ? <Check className="h-3 w-3 mr-1" /> : null}
                                Niveau {levelNum}: {LEVEL_LABELS[levelNum]?.label}
                              </Badge>
                            </div>
                            <div className="space-y-1.5">
                              {conditions.map(cond => (
                                <label key={cond.key}
                                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${editData[levelKey]?.[cond.key] ? 'bg-emerald-50' : 'hover:bg-neutral-50'}`}
                                  data-testid={`cond-${levelKey}-${cond.key}`}>
                                  <Switch checked={editData[levelKey]?.[cond.key] || false} onCheckedChange={() => toggleCondition(levelKey, cond.key)} disabled={!prevPassed} />
                                  <span className="text-xs text-neutral-700">{cond.label}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="p-4 border-t border-neutral-100 flex items-center justify-between">
                      <p className="text-[10px] text-neutral-400">
                        {selectedIndicator.updated_at ? `Laatst: ${new Date(selectedIndicator.updated_at).toLocaleString('nl-NL')}` : 'Nog niet beoordeeld'}
                      </p>
                      <Button onClick={saveAssessment} disabled={saving} className="bg-black text-white text-xs" data-testid="save-indicator-btn">
                        {saving ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
                        Opslaan
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>

        </div>
      </div>
    </MainLayout>
  );
};

export default MaturityPage;
